<template lang="pug">
  .transfer-chat-configuration
    .settings-container
      .header-config-container
        .field-label.field-label--with-switch
          span {{ $t(`${BASE_TRANSLATION_PATH}.summary.summary`) }}
          aiq-switch.margin-left-10(v-model="localTransferChatConfigs.summary.enabled")
        template(v-if="localTransferChatConfigs.summary.enabled")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.summary.title`) }}
            .inline-label-box
              aiq-input(v-model="localTransferChatConfigStrings.summary.title"
                :placeholder="$t(`${BASE_TRANSLATION_PATH}.summary.title_placeholder`)"
                size="small")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.summary.title_icon`) }}
            .inline-label-box(v-if="localTransferChatConfigs.summary.icon_url")
              aiq-image.summary-icon-image(
                :src="localTransferChatConfigs.summary.icon_url"
                :key="localTransferChatConfigs.summary.icon_url")
            .inline-label-box
              .upload-button
                aiq-upload.no-margin(
                  v-model="localTransferChatConfigs.summary.icon_url"
                  :http-request="upload()"
                  :action="uploadUrl"
                  accept="image/*"
                  :headers="headers"
                  :show-file-list="false"
                  :disabled="!uploadEnabled"
                  :on-success="(res) => onHandleUploadSuccess(res, ['summary', 'icon_url'])"
                  ref="summaryTitleIconUploader")
                  aiq-button.upload-btn(size="small" :disabled="!uploadEnabled") {{ $t(`global.upload`) }}
                aiq-button.delete-btn(
                  v-if="localTransferChatConfigs.summary.icon_url"
                  size="small"
                  type="danger"
                  @click="cleanExternalConversationSummaryIcon") {{ $t(`global.delete`) }}

    .settings-container
      .header-config-container
        .field-label.field-label--with-switch
          span {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_button.title`) }}
          aiq-switch.margin-left-10(v-model="localTransferChatConfigs.transfer_back_button.enabled")
        template(v-if="localTransferChatConfigs.transfer_back_button.enabled")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_button.button_text`) }}
            .inline-label-box
              aiq-input(v-model="localTransferChatConfigStrings.transfer_back_button.text"
                size="small")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_button.event_hook`) }}
            .inline-label-box
              aiq-input(v-model="localTransferChatConfigs.transfer_back_button.trigger_event"
                size="small")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_button.target`) }}
            .inline-label-box
              aiq-select(v-model="localTransferChatConfigs.transfer_back_button.channels" multiple placeholder="Select" :reserve-keyword="false")
                aiq-option(v-for="item in targetOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value")

    .settings-container
      .header-config-container
        .field-label.field-label--with-switch
          span {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_hamburger_menu.title`) }}
          aiq-switch.margin-left-10(v-model="localTransferChatConfigs.transfer_back_hamburger_menu.enabled")
        template(v-if="localTransferChatConfigs.transfer_back_hamburger_menu.enabled")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_hamburger_menu.text`) }}
            .inline-label-box
              aiq-input(v-model="localTransferChatConfigStrings.transfer_back_hamburger_menu.text"
                size="small")
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_hamburger_menu.icon`) }}
            .inline-label-box(v-if="localTransferChatConfigs.transfer_back_hamburger_menu.icon_url")
              img.terms-conditions-icon-svg(
                :src="localTransferChatConfigs.transfer_back_hamburger_menu.icon_url"
                :key="localTransferChatConfigs.transfer_back_hamburger_menu.icon_url"
                alt="")
            .inline-label-box
              .upload-button
                aiq-upload.no-margin(
                  v-model="localTransferChatConfigs.transfer_back_hamburger_menu.icon_url"
                  :http-request="upload()"
                  :action="uploadUrl"
                  accept="image/*"
                  :headers="headers"
                  :show-file-list="false"
                  :disabled="!uploadEnabled"
                  :on-success="(res) => onHandleUploadSuccess(res, ['transfer_back_hamburger_menu', 'icon_url'])"
                  ref="hamburgerMenuIconUploader")
                  aiq-button.upload-btn(size="small" :disabled="!uploadEnabled") {{ $t(`global.upload`) }}
                aiq-button.delete-btn(
                  v-if="localTransferChatConfigs.transfer_back_hamburger_menu.icon_url"
                  size="small"
                  type="danger"
                  @click="cleanHamburgerMenuIcon") {{ $t(`global.delete`) }}
          .sub-field
            label {{ $t(`${BASE_TRANSLATION_PATH}.transfer_back_hamburger_menu.target`) }}
            .inline-label-box
              aiq-select(v-model="localTransferChatConfigs.transfer_back_hamburger_menu.channels" multiple placeholder="Select" :reserve-keyword="false")
                aiq-option(v-for="item in targetOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value")
    .settings-container
      .settings__item
        .save-buttons__container.settings__item
          aiq-button(:disabled="isTransferChatConfigurationPristine" @click="resetTransferChatConfiguration") {{ $t(`global.cancel`) }}
          aiq-button.save__button(type="primary" :disabled="isTransferChatConfigurationPristine" @click="saveTransferChatConfiguration") {{ $t(`global.save`) }}
</template>

<script>
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { hosts } from '@/config/api.routes';
import { HAMBURGER_MENU_SELECTION_OPTIONS } from '@/constants/settings';

/** Defaults aligned with transfer_chat_configuration API schema. */
const DEFAULT_SUMMARY = () => ({
  enabled: false,
  title_langkey: '',
  icon_url: '',
});

const DEFAULT_TRANSFER_BACK_BUTTON = () => ({
  enabled: false,
  text_langkey: '',
  icon_url: '',
  trigger_event: '',
  channels: [],
});

const DEFAULT_TRANSFER_BACK_HAMBURGER_MENU = () => ({
  enabled: false,
  text_langkey: '',
  icon_url: '',
  channels: [],
});

const TRANSFER_CHAT_CONFIG_STRING_LANG_KEY_MAP = {
  'summary.title': 'summary.title_langkey',
  'transfer_back_button.text': 'transfer_back_button.text_langkey',
  'transfer_back_hamburger_menu.text': 'transfer_back_hamburger_menu.text_langkey',
};

export default {
  name: 'transfer-chat-configuration',
  props: {
    configs: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['save'],
  data() {
    return {
      BASE_TRANSLATION_PATH: 'settings_tab.channels_tab.webchat_ui.transfer_chat_configuration',
      localTransferChatConfigs: this.cloneLocalTransferChatConfigs(this.configs),
      localTransferChatConfigStrings: this.cloneLocalTransferChatConfigStrings(
        this.webchatLanguageStrings || {},
        this.configs,
      ),
      uploadUrl: `${hosts.management}files/upload`,
      uploadEnabled: true,
      targetOptions: cloneDeep(HAMBURGER_MENU_SELECTION_OPTIONS),
      headers: {
        Authorization: this.$store.getters['agent/authToken'],
      },
    };
  },
  computed: {
    ...mapState({
      webchatLanguageStrings: state => state.configs.language_strings,
    }),
    isTransferChatConfigurationPristine() {
      const baselineStrings = this.cloneLocalTransferChatConfigStrings(
        this.webchatLanguageStrings || {},
        this.configs,
      );
      return isEqual(
        get(this.localTransferChatConfigs, 'summary', {}),
        get(this.configs, 'summary', {}),
      ) && isEqual(
        get(this.localTransferChatConfigs, 'transfer_back_button', {}),
        get(this.configs, 'transfer_back_button', {}),
      ) && isEqual(
        get(this.localTransferChatConfigs, 'transfer_back_hamburger_menu', {}),
        get(this.configs, 'transfer_back_hamburger_menu', {}),
      ) && isEqual(this.localTransferChatConfigStrings, baselineStrings);
    },
  },
  watch: {
    configs(newVal) {
      this.localTransferChatConfigs = this.cloneLocalTransferChatConfigs(newVal);
      this.localTransferChatConfigStrings = this.cloneLocalTransferChatConfigStrings(
        this.webchatLanguageStrings || {},
        newVal,
      );
    },
    webchatLanguageStrings: {
      handler(newVal) {
        this.localTransferChatConfigStrings = this.cloneLocalTransferChatConfigStrings(newVal || {}, this.configs);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    /**
     * @param key String like TRANSFER_CHAT_CONFIG_STRING_KEYS
     * @returns String value of the key like "webchat.chat_disclaimer.link_label"
     */
    getTCConfigStringKeyPath(key) {
      return get(this.localTransferChatConfigs, key, '');
    },
    cloneLocalTransferChatConfigs(newConfig) {
      const cfg = cloneDeep(newConfig || {});
      set(cfg, 'summary', { ...DEFAULT_SUMMARY(), ...get(cfg, 'summary', {}) });
      set(cfg, 'transfer_back_button', { ...DEFAULT_TRANSFER_BACK_BUTTON(), ...get(cfg, 'transfer_back_button', {}) });
      set(cfg, 'transfer_back_hamburger_menu', { ...DEFAULT_TRANSFER_BACK_HAMBURGER_MENU(), ...get(cfg, 'transfer_back_hamburger_menu', {}) });

      return cfg;
    },
    /**
     * @param {object} languageStringsRoot full `state.configs.language_strings`
     * @param {object} transferConfigs transfer_chat_configs (prop `configs` or local clone)
     */
    cloneLocalTransferChatConfigStrings(languageStringsRoot, transferConfigs) {
      const sourceConfigs = transferConfigs || this.localTransferChatConfigs;
      const cfgStrings = {};
      Object.keys(TRANSFER_CHAT_CONFIG_STRING_LANG_KEY_MAP).forEach(key => {
        const langKeyPath = TRANSFER_CHAT_CONFIG_STRING_LANG_KEY_MAP[key];
        const pathInLocaleFile = get(sourceConfigs, langKeyPath, '');

        const localeString = typeof pathInLocaleFile === 'string' && pathInLocaleFile.length > 0
          ? get(languageStringsRoot, pathInLocaleFile, '')
          : '';
        set(cfgStrings, key, localeString);
      });
      return cfgStrings;
    },
    buildTransferChatConfigPayload() {
      return Object.keys(TRANSFER_CHAT_CONFIG_STRING_LANG_KEY_MAP).reduce((acc, key) => {

        const langKeyPath = this.getTCConfigStringKeyPath(TRANSFER_CHAT_CONFIG_STRING_LANG_KEY_MAP[key]);
        if (!langKeyPath) {
          return acc;
        }
        set(acc, langKeyPath, get(this.localTransferChatConfigStrings, key, ''));

        return acc;
      }, {});
    },
    upload() {
      return async(data) => {
        try {
          return await this.$store.dispatch('files/uploadPublicManagementFile', { file: data.file });
        } catch (err) {
          this.onUploadFailed(get(err, 'response.data.error'));
        }
      };
    },
    onHandleUploadSuccess(res, urlPath) {
      if (!res?.url) {
        return this.onUploadFailed();
      }
      set(this.localTransferChatConfigs, urlPath, res.url);
      this.resetUploading();
      this.$aiq.notify.success('Successful');
    },
    onUploadFailed(errorMsg = 'Failed') {
      this.$aiq.notify.error(errorMsg);
      this.resetUploading();
      this.$refs.summaryTitleIconUploader &&
        this.$refs.summaryTitleIconUploader.clearFiles();
      this.$refs.hamburgerMenuIconUploader &&
        this.$refs.hamburgerMenuIconUploader.clearFiles();
    },
    resetUploading() {
      this.uploadEnabled = true;
    },
    cleanExternalConversationSummaryIcon() {
      this.localTransferChatConfigs.summary.icon_url = '';
    },
    cleanHamburgerMenuIcon() {
      this.localTransferChatConfigs.transfer_back_hamburger_menu.icon_url = '';
    },
    resetTransferChatConfiguration() {
      this.localTransferChatConfigs = this.cloneLocalTransferChatConfigs(this.configs);
      this.localTransferChatConfigStrings = this.cloneLocalTransferChatConfigStrings(
        this.webchatLanguageStrings || {},
        this.configs,
      );
    },
    saveTransferChatConfiguration() {
      this.$emit('save', {
        transferChatConfig: this.localTransferChatConfigs,
        configStrings: this.buildTransferChatConfigPayload(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";

.transfer-chat-configuration {
  * {
    margin: 5px 0px;
  }

  .no-margin {
    padding-right: 10px;
    margin: 0px;
  }

  .settings-container {
    display: flex;

    .settings__item {
      align-self: flex-start;
      flex-grow: 1;
      min-width: 589px;
    }
  }

  .field-label {
    color: #475669;
    letter-spacing: 0;
    font-family: $aiq-font-family;
    font-size: 14px;
    font-weight: 300;

    &--with-switch {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  .margin-left-10 {
    margin-left: 10px;
  }

  .header-config-container {
    width: 482px;

    .sub-field {
      padding-left: 14px;
    }

    &--split {
      width: 100%;
      max-width: 980px;
    }
  }

  .inline-label-box {
    display: flex;
  }

  .upload-button {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }

  .save-buttons__container {
    background-color: var(--aiq-color-white);
    bottom: 0;
    display: flex;
    justify-content: flex-end;
    left: 0;
    margin-bottom: 0px;
    padding: 5px 8px 0px 0px;
    width: calc(100% - 4px);

    * {
      margin: 0px 5px;
    }

    .save__button {
      margin-right: 0px;
    }
  }

  .summary-icon-image {
    max-width: 40px;
    max-height: 40px;
  }

  .terms-conditions-icon-svg {
    max-width: 40px;
    max-height: 40px;
    object-fit: contain;
  }
}
</style>
